import { useState, useCallback } from "react";
import apiClient from "utils/apiClient";

// Base URL for the Gateway Service
const GATEWAY_SERVICE_BASE_URL = 'https://dev.oliveeb.com';

export default function useAppVersions() {
  const [appVersions, setAppVersions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Get all app versions
  const getAllAppVersions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get(`${GATEWAY_SERVICE_BASE_URL}/api/v1/app-versions`);
      setAppVersions(response.data || []);
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message || "Failed to fetch app versions";
      console.error("[useAppVersions:getAllAppVersions]", err);
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  // Get app version by ID
  const getAppVersionById = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get(`${GATEWAY_SERVICE_BASE_URL}/api/v1/app-versions/${id}`);
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message || "Failed to fetch app version";
      console.error("[useAppVersions:getAppVersionById]", err);
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  // Get app versions by app name
  const getAppVersionsByApp = useCallback(async (appName) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get(`${GATEWAY_SERVICE_BASE_URL}/api/v1/app-versions/by-app`, {
        params: { appName }
      });
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message || "Failed to fetch app versions by app";
      console.error("[useAppVersions:getAppVersionsByApp]", err);
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  // Get current version for app
  const getCurrentVersion = useCallback(async (appName) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get(`${GATEWAY_SERVICE_BASE_URL}/api/v1/app-versions/current`, {
        params: { appName }
      });
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message || "Failed to fetch current version";
      console.error("[useAppVersions:getCurrentVersion]", err);
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  //Create new app version
  const createAppVersion = useCallback(async (versionData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.post(`${GATEWAY_SERVICE_BASE_URL}/api/v1/app-versions`, versionData);
      // Refresh the list after creation
      await getAllAppVersions();
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message || "Failed to create app version";
      console.error("[useAppVersions:createAppVersion]", err);
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [getAllAppVersions]);

  // Update app version
  const updateAppVersion = useCallback(async (id, versionData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.put(`${GATEWAY_SERVICE_BASE_URL}/api/v1/app-versions/${id}`, versionData);

      // Refresh the list after update
      await getAllAppVersions();
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message || "Failed to update app version";
      console.error("[useAppVersions:updateAppVersion]", errorMessage);
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [getAllAppVersions]);

  // Change app version status
  const changeAppVersionStatus = useCallback(async (id, newStatus) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.put(`${GATEWAY_SERVICE_BASE_URL}/api/v1/app-versions/${id}/change-status`, {
        newStatus: newStatus
      });

      // Refresh the list after status change
      await getAllAppVersions();
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message || "Failed to change app version status";
      console.error("[useAppVersions:changeAppVersionStatus]", errorMessage);
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [getAllAppVersions]);

  // Delete app version
  const deleteAppVersion = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      await apiClient.delete(`${GATEWAY_SERVICE_BASE_URL}/api/v1/app-versions/${id}`);
      // Refresh the list after deletion
      await getAllAppVersions();
      return true;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message || "Failed to delete app version";
      console.error("[useAppVersions:deleteAppVersion]", err);
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [getAllAppVersions]);

  return {
    appVersions,
    loading,
    error,
    getAllAppVersions,
    getAppVersionById,
    getAppVersionsByApp,
    getCurrentVersion,
    createAppVersion,
    updateAppVersion,
    changeAppVersionStatus,
    deleteAppVersion,
  };
}